// @flow
import { DEFAULT_TIMEZONE, NUMBER_OF_DAYS_DISPLAYED } from './constants';

import { createSelector } from 'reselect';
import { fromJS } from 'immutable';
import { identity } from 'lodash/fp';
import moment from 'moment-timezone';
import { getHublessProducts } from '~/core/state/products/selectors';
import { getHub as getHubDevice, getHubHasCapability } from '~/core/state/devices/selectors';
import { capabilities } from '~/core/constants/devices';

export const getHub = (state: {}): fromJS => state.hub || fromJS({});

export const getCurrentTime = createSelector([getHub], hub => hub.get('currentTime'));

export const getTimezone = createSelector([getHub], hub => hub.get('timezone'));

export const getHasHub = createSelector([getHub], hub => hub.get('hasHub'));

export const getCurrentTimeWithTimezone = createSelector(getHub, getCurrentTime, (hub, currentTime) =>
    moment(currentTime).tz(hub.get('timezone', DEFAULT_TIMEZONE))
);

export const getMomentWithHubTimezone = createSelector([getHub], hub =>
    moment().tz(hub.get('timezone', DEFAULT_TIMEZONE))
);

const roundMinutesToNext5 = date =>
    date
        .clone()
        .minutes(Math.ceil((date.minutes() + 1) / 5) * 5)
        .seconds(0)
        .milliseconds(0);

export const getHubTimeToNearest5Minutes = createSelector([getMomentWithHubTimezone], roundMinutesToNext5);

export const getHubDate = createSelector([getCurrentTime, getTimezone], (unixTime, timezone) =>
    moment.unix(unixTime / 1000).tz(timezone)
);

export const getHubTodayNumber = createSelector([getHubDate], now => now.day());

export const getHubMidnight = createSelector([getHubDate], now => now.clone().startOf('day'));

export const getHubMinsSinceMidnight = createSelector([getHubDate], now => now.hours() * 60 + now.minutes());

const incrementDate = (day, increment = 1, unit = 'day') => day.clone().add(increment, unit);
const displayTime = (day, increment = 0) => incrementDate(day, increment, 'm').format('LT');

export const makeGetNextScheduleEventDisplay = (event: typeof fromJS) =>
    createSelector(getHubMidnight, midnight => {
        const state = event.getIn(['value', 'status'], 'OFF');
        const target = event.getIn(['value', 'target']);
        const start = event.get('start', '0');
        const end = event.get('end', '200');
        const nextHeatTarget = event.getIn(['value', 'heat']);
        const nextCoolTarget = event.getIn(['value', 'cool']);

        return {
            nextStatus: state,
            nextStartTime: displayTime(midnight, start),
            nextEndTime: displayTime(midnight, end),
            nextTarget: target,
            nextHeatCoolTargets: [nextCoolTarget, nextHeatTarget].filter(identity)
        };
    });

export const getPmzStatus = createSelector(getHub, hub => hub.get('pmzStatus'));

export const getTodayAsString = createSelector([getCurrentTime, getTimezone], (currentTime, timezone) =>
    moment.tz(currentTime, timezone).format('YYYY-MM-DD')
);

export const getStartOfTodayMoment = createSelector([getCurrentTime, getTimezone], (currentTime, timezone) =>
    moment.tz(currentTime, timezone).startOf('day')
);

export const getStartOfDayFromLastWeekMoment = createSelector([getCurrentTime, getTimezone], (currentTime, timezone) =>
    moment
        .tz(currentTime, timezone)
        .subtract(NUMBER_OF_DAYS_DISPLAYED - 1, 'days')
        .startOf('day')
);

export const getStartOfWeekFromLastMonthMoment = createSelector(
    [getCurrentTime, getTimezone],
    (currentTime, timezone) =>
        moment
            .tz(currentTime, timezone)
            .subtract(1, 'month')
            .startOf('week')
);

export const getEndOfWeekMoment = createSelector([getCurrentTime, getTimezone], (currentTime, timezone) =>
    moment.tz(currentTime, timezone).endOf('week')
);

export const getRecentMonthFullWeeksAsArray = createSelector(
    [getStartOfWeekFromLastMonthMoment, getEndOfWeekMoment],
    (startPeriod, endPeriod) => {
        const period = [];
        const currentDay = endPeriod.clone();

        while (!currentDay.isBefore(startPeriod)) {
            period.push(currentDay.format('YYYY-MM-DD'));
            currentDay.subtract(1, 'days');
        }
        return period;
    }
);

const getHubActions2Capability = state => getHubHasCapability(state, capabilities.ACTIONS_2);

export const canUseActions2 = createSelector(
    getHubDevice,
    getHublessProducts,
    getHubActions2Capability,
    (hub, hublessProducts, hubHasActions2Capability) => (!hub && hublessProducts.size > 0) || hubHasActions2Capability
);



// WEBPACK FOOTER //
// ./src/core/state/hub/selectors.js